import React, {
  useState,
  useCallback,
  useEffect,
  useContext,
  useMemo,
} from 'react';
import { WEB_MAX_CONTENT_WIDTH } from '../../../theme/webLayoutWidths';

import {
  View,
  Text,
  SectionList,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  Platform,
} from 'react-native';

import AsyncStorage from '@react-native-async-storage/async-storage';
import { useFocusEffect } from '@react-navigation/native';
import SearchContext from '../../../Context/SearchContext';
import { GTFonts, theme, useLayoutSize } from '../../../theme';
import { fs, s, v } from '../../../utils/libs';
import { shouldUseWidthConstraint } from '../../../utils/layoutUtils';

export interface Note {
  id: string;
  title: string;
  content: string;
  isPinned: boolean;
  createdAt: string;
  updatedAt: string;
  subtitle?: string;
}

const NotesScreen = ({ navigation }: { navigation: any }) => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const { width, height } = useLayoutSize();
  const useWidthConstraint = shouldUseWidthConstraint(width, height);
  const styles = useMemo(
    () => createStyles(theme, { useWidthConstraint }),
    [theme, useWidthConstraint],
  );
  const { searchNotesQuery, setSearchNotesQuery } = useContext(SearchContext);

  useEffect(() => {
    setSearchQuery(searchNotesQuery || '');
  }, [searchNotesQuery]);

  const loadNotes = useCallback(async () => {
    try {
      const savedNotes = await AsyncStorage.getItem('notes');
      if (savedNotes) {
        setNotes(JSON.parse(savedNotes));
      }
    } catch (error) {
      console.error('Error loading notes:', error);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      loadNotes();
    }, [loadNotes]),
  );

  const isToday = (dateString: string) => {
    const today = new Date();
    const date = new Date(dateString);
    return date.toDateString() === today.toDateString();
  };

  const groupNotes = (notes: Note[]) => {
    const todayNotes = notes.filter(note => isToday(note.updatedAt));
    const olderNotes = notes.filter(note => !isToday(note.updatedAt));

    return [
      { title: 'Today', data: todayNotes },
      { title: 'Older', data: olderNotes },
    ].filter(section => section.data.length > 0);
  };

  const renderSectionHeader = ({
    section: { title },
  }: {
    section: { title: string };
  }) => <Text style={styles.sectionTitle}>{title}</Text>;

  const renderNoteItem = ({ item }: { item: Note }) => (
    <Pressable
      style={({ pressed, hovered }: any) => [
        styles.noteItem,
        Platform.OS === 'web' && styles.noteItemWeb,
        Platform.OS === 'web' && hovered && styles.noteItemHovered,
        pressed && { opacity: 0.85 },
      ]}
      onPress={() => navigation.navigate('ViewNote', { noteId: item.id })}>
      <Text style={styles.noteContent} numberOfLines={3} ellipsizeMode="tail">
        {item.content}
      </Text>
      {item.subtitle && (
        <Text
          style={styles.noteSubtitle}
          numberOfLines={1}
          ellipsizeMode="tail">
          {item.subtitle}
        </Text>
      )}
      <Text style={styles.noteDate}>{formatDate(item.updatedAt)}</Text>
    </Pressable>
  );

  const renderPlaceholderNote = () => (
    <Pressable
      style={({ pressed, hovered }: any) => [
        styles.noteItem,
        styles.placeholderNote,
        Platform.OS === 'web' && styles.noteItemWeb,
        Platform.OS === 'web' && hovered && styles.noteItemHovered,
        pressed && { opacity: 0.85 },
      ]}
      onPress={() => navigation.navigate('NewNote')}>
      <Text style={[styles.noteContent, styles.placeholderText]}>
        Tap to create your first note...
      </Text>
      <Text style={[styles.noteDate, styles.placeholderText]}>
        {/* Start writing */}
      </Text>
    </Pressable>
  );

  const formatDate = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    if (date.toDateString() === now.toDateString()) {
      return 'Today';
    }
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    return `${date.getDate()} ${months[date.getMonth()]}`;
  };

  const filteredNotes = notes.filter(note =>
    searchQuery.length > 1
      ? note.content.toLowerCase().includes(searchQuery.toLowerCase())
      : true,
  );

  useEffect(() => {
    if (Platform.OS === 'web') return;
    navigation.setOptions({
      headerSearchBarOptions: {
        onChangeText: (event: any) => {
          const value = event.nativeEvent.text;
          setSearchQuery(value);
          setSearchNotesQuery(value);
        },
      },
    });
  }, [navigation, setSearchNotesQuery]);
  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>All notes</Text>
        <Text style={styles.noteCount}>{notes.length}</Text>
      </View>

      {filteredNotes.length > 0 ? (
        <SectionList
          sections={groupNotes(filteredNotes)}
          renderItem={renderNoteItem}
          renderSectionHeader={renderSectionHeader}
          keyExtractor={item => item.id}
        />
      ) : (
        <View style={styles.notesList}>
          {searchQuery ? (
            <View style={styles.emptyStateContainer}>
              <Text style={styles.emptyStateText}>
                No matching notes found.
              </Text>
            </View>
          ) : (
            renderPlaceholderNote()
          )}
        </View>
      )}

      {Platform.OS === 'web' ? (
        <Pressable
          style={({ pressed }) => [
            styles.floatingButton,
            pressed && styles.floatingButtonPressed,
          ]}
          onPress={() => navigation.navigate('NewNote')}>
          <Text style={styles.floatingButtonText}>+</Text>
        </Pressable>
      ) : (
        <TouchableOpacity
          style={styles.floatingButton}
          onPress={() => navigation.navigate('NewNote')}>
          <Text style={styles.floatingButtonText}>+</Text>
        </TouchableOpacity>
      )}
    </View>
  );
};

const createStyles = (theme: any, opts: { useWidthConstraint: boolean }) => {
  const { useWidthConstraint } = opts;
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.colors.background,
      paddingHorizontal: s(8),
      marginVertical: v(5),
      ...(useWidthConstraint && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
        paddingHorizontal: s(24),
      }),
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 0,
    },
    title: {
      fontSize: fs(26),
      fontFamily: GTFonts.semibold,
      color: theme.colors.shadow,
    },
    noteCount: {
      fontSize: fs(16),
      color: theme.colors.graphite,
    },
    searchInput: {
      backgroundColor: theme.colors.borderGray200,
      borderRadius: s(8),
      padding: s(12),
      marginBottom: v(16),
      color: theme.colors.shadow,
    },
    sectionTitle: {
      fontSize: fs(14),
      color: theme.colors.graphite,
      marginTop: v(16),
      marginBottom: v(8),
    },
    noteItem: {
      backgroundColor: theme.colors.surface,
      borderRadius: s(5),
      padding: s(8),
      marginHorizontal: s(Platform.OS === 'web' ? 0.5 : 1),
      marginBottom: v(12),
      shadowColor: theme.colors.shadow,
      shadowOffset: { width: 0, height: v(1) },
      shadowOpacity: 0.1,
      shadowRadius: s(2),
      elevation: 2,
    },
    noteContent: {
      fontSize: fs(16),
      fontFamily: GTFonts.regular,
      color: theme.colors.shadow,
      marginBottom: v(4),
      lineHeight: fs(22),
    },
    noteSubtitle: {
      fontSize: fs(14),
      fontFamily: GTFonts.regular,
      color: theme.colors.graphite,
      marginBottom: v(4),
    },
    noteDate: {
      fontSize: fs(12),
      lineHeight: fs(14),
      fontFamily: GTFonts.regular,
      color: theme.colors.graphite,
    },
    floatingButton: {
      position: 'absolute',
      bottom: Platform.OS === 'web' ? v(24) : v(5),
      right: Platform.OS === 'web' ? s(24) : s(15),
      width: Platform.OS === 'web' ? s(60) : s(46),
      height: Platform.OS === 'web' ? v(60) : v(46),
      borderRadius: Platform.OS === 'web' ? theme.borderRadius.md : s(8),
      backgroundColor: theme.colors.success,
      alignItems: 'center',
      justifyContent: 'center',
    } as any,
    floatingButtonPressed: {
      transform: [{ scale: 0.95 }],
    },
    floatingButtonText: {
      fontSize: fs(24),
      color: theme.colors.textInverse,
      fontWeight: 'bold',
    },
    emptyStateContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
    },
    emptyStateText: {
      fontSize: fs(16),
      fontFamily: GTFonts.regular,
      color: theme.colors.graphite,
      textAlign: 'center',
    },
    placeholderNote: {
      marginTop: v(20),
    },
    placeholderText: {
      color: theme.colors.graphite,
      fontStyle: 'italic',
    },
    notesList: {
      flex: 1,
    },
    noteItemWeb: {
      ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : {}),
    },
    noteItemHovered: {
      backgroundColor: 'rgba(0,0,0,0.04)',
    },
  });
};

export default NotesScreen;
